import { useEffect } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import Loader from '../Loader';
import { hasModalBackground } from '../../hooks/useSafeBackNavigation';

interface ModalRouteRedirectProps {
    fallbackBackground: Pick<Location, 'pathname'> & Partial<Pick<Location, 'search' | 'hash'>>;
    children: React.ReactNode;
}

/** Re-open a modal route with a frozen background page (refresh/direct URL fallback). */
const ModalRouteRedirect = ({ fallbackBackground, children }: ModalRouteRedirectProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const hasBackground = hasModalBackground(location);

    useEffect(() => {
        if (hasBackground) {
            return;
        }

        const background: Location = {
            pathname: fallbackBackground.pathname,
            search: fallbackBackground.search ?? '',
            hash: fallbackBackground.hash ?? '',
            state: null,
            key: 'default',
        };

        navigate(`${location.pathname}${location.search}${location.hash}`, {
            replace: true,
            state: {
                ...(typeof location.state === 'object' && location.state !== null ? location.state : {}),
                background,
            },
        });
    }, [fallbackBackground.hash, fallbackBackground.pathname, fallbackBackground.search, hasBackground, location, navigate]);

    if (hasBackground) {
        return <>{children}</>;
    }

    return <Loader />;
};

export default ModalRouteRedirect;
